import { useState } from 'react';
import { motion } from 'framer-motion';
import { useSelector } from 'react-redux';
import 'yet-another-react-lightbox/styles.css';
import Lightbox from 'yet-another-react-lightbox';
import PageHeader from '../components/PageHeader';
import 'yet-another-react-lightbox/plugins/captions.css';
import Zoom from 'yet-another-react-lightbox/plugins/zoom';
import Captions from 'yet-another-react-lightbox/plugins/captions';
import { Box, Typography, Paper, CircularProgress } from '@mui/material';

import {
  Timeline,
  TimelineItem,
  TimelineSeparator,
  TimelineConnector,
  TimelineContent,
  TimelineDot,
  TimelineOppositeContent,
} from '@mui/lab';
import SportsCar from '@mui/icons-material/DirectionsCar';
import Flag from '@mui/icons-material/Flag';
import Trophy from '@mui/icons-material/EmojiEvents';
import Group from '@mui/icons-material/Group';
import Event from '@mui/icons-material/Event';
import Star from '@mui/icons-material/StarBorder';
import HistoryVision from '../components/HistorySection/Vision';

// Icon mapping for different types of events
const iconMap = {
  'car': <SportsCar />,
  'flag': <Flag />,
  'trophy': <Trophy />,
  'group': <Group />,
  'event': <Event />,
  'star': <Star />
};


function History() {
  const [isOpen, setIsOpen] = useState(false);
  const [photoIndex, setPhotoIndex] = useState(0);
  const [hoveredIndex, setHoveredIndex] = useState(null);
  const { historyData } = useSelector((state) => state.history);
  const [imageLoadingStates, setImageLoadingStates] = useState({});
  const loading = historyData?.status === 'loading' || historyData?.status === 'idle';

  const preloadImage = (url) => {
    if (!imageLoadingStates[url]) {
      setImageLoadingStates(prev => ({ ...prev, [url]: false }));
      const img = new Image();
      img.onload = () => {
        setImageLoadingStates(prev => ({ ...prev, [url]: true }));
      };
      img.src = url;
    }
  };

  return (
    <Box>
      <PageHeader
        title="Our History"
        backgroundImage="/images/headers/history_bg.jpeg"
        subtitle="Two decades of passion, innovation, and grassroots motorsport development in Asia"
      />

      {/* History Items Section */}
      <Box>
        {loading ? (
          <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
            <CircularProgress />
          </Box>
        ) : (
          <Timeline position="alternate">
            {historyData?.item?.map((event, index) => {
              return (
                <TimelineItem key={index}>
                  <TimelineOppositeContent sx={{ py: 3 }}>
                    <Typography
                      variant="h3"
                      sx={{
                        fontFamily: 'Bebas Neue',
                        color: 'primary.main',
                        fontSize: { xs: '2rem', md: '2.5rem' },
                      }}
                    >
                      {event.year}
                    </Typography>
                  </TimelineOppositeContent>
                  <TimelineSeparator>
                    <TimelineDot
                      sx={{
                        bgcolor: hoveredIndex === index ? 'secondary.main' : 'primary.main',
                        transition: 'all 0.3s ease-in-out',
                        p: 2,
                      }}
                    >
                      {iconMap[event.icon] || <Event />}
                    </TimelineDot>
                    {index < historyData?.item?.length - 1 && <TimelineConnector />}
                  </TimelineSeparator>
                  <TimelineContent sx={{ py: 3 }}>
                    <Paper
                      component={motion.div}
                      whileHover={{ y: -4 }}
                      onMouseEnter={() => setHoveredIndex(index)}
                      onMouseLeave={() => setHoveredIndex(null)}
                      sx={{
                        p: 3,
                        transition: 'all 0.3s ease-in-out',
                        borderLeft: 3,
                        borderColor: hoveredIndex === index ? 'secondary.main' : 'primary.main',
                        '&:hover': {
                          boxShadow: 4
                        },
                        overflow: 'hidden'
                      }}
                    >
                      {event.imageUrl && (
                        <Box
                          component="img"
                          src={event.imageUrl}
                          alt={event.title}
                          onClick={() => {
                            setPhotoIndex(index);
                            setIsOpen(true);
                            preloadImage(event.imageUrl);
                          }}
                          sx={{
                            display: { xs: 'none', sm: 'block' },
                            width: '100%',
                            height: 200,
                            objectFit: 'cover',
                            mb: 2,
                            borderRadius: 1,
                            filter: 'brightness(0.9)',
                            transition: 'all 0.3s ease-in-out',
                            cursor: 'pointer',
                            '&:hover': {
                              filter: 'brightness(1.1)'
                            }
                          }}
                        />
                      )}
                      <Typography variant="h6" sx={{ mb: 1, fontWeight: 600 }}>
                        {event.title}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {event.description}
                      </Typography>
                    </Paper>
                  </TimelineContent>
                </TimelineItem>
              );
            })}
          </Timeline>
        )}
      </Box>


      <HistoryVision />
      <Lightbox
        open={isOpen}
        close={() => setIsOpen(false)}
        index={photoIndex}
        slides={(historyData?.item || []).map(event => ({
          src: event.imageUrl,
          title: event.title,
          description: event.description
        }))}
        plugins={[Zoom, Captions]}
        on={{
          view: ({ index }) => setPhotoIndex(index)
        }}
        animation={{ zoom: 500 }}
        zoom={{
          maxZoomPixelRatio: 3,
          zoomInMultiplier: 2
        }}
        carousel={{
          spacing: 0.5,
          padding: 0,
          imageFit: 'contain'
        }}
      />
    </Box>
  );
}

export default History;
